import React, { type FC } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import EditorPanelIcon from '@atlaskit/icon/core/information';

import type { ContentType } from '../../../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator';

import { InfoBox } from './InfoBox';

const shareDialogI18n = defineMessages({
	publishPageWarning: {
		id: 'share-and-restrict-dialog.publish-modal-warning.publish-page',
		defaultMessage: 'Choose the access settings to apply when publishing this page.',
		description:
			'A message that explains that the page restrictions settings in the access dialog will not apply until the page is first published.',
	},
	publishBlogWarning: {
		id: 'share-and-restrict-dialog.publish-modal-warning.publish-blog',
		defaultMessage: 'Choose the access settings to apply when publishing this blog.',
		description:
			'A message that explains that the page restrictions settings in the access dialog will not apply until the blog is first published.',
	},
	updatePageWarning: {
		id: 'share-and-restrict-dialog.publish-modal-warning.update-page',
		defaultMessage: 'Choose the access settings to apply when updating this page.',
		description:
			'A message that explains that the page restrictions settings in the access dialog will not apply until the page is updated (published a subsequent time).',
	},
	updateBlogWarning: {
		id: 'share-and-restrict-dialog.publish-modal-warning.update-blog',
		defaultMessage: 'Choose the access settings to apply when updating this blog.',
		description:
			'A message that explains that the page restrictions settings in the access dialog will not apply until the blog is updated (published a subsequent time).',
	},
});

export type PublishModalInfoBoxProps = {
	isUnpublishedDraft: boolean;
	contentType: ContentType;
};

export const PublishModalInfoBox: FC<PublishModalInfoBoxProps> = ({
	isUnpublishedDraft,
	contentType,
}: PublishModalInfoBoxProps) => {
	const { formatMessage } = useIntl();
	const shareDialogInfoTextI18n = (() => {
		if (contentType === 'blogpost') {
			return isUnpublishedDraft
				? shareDialogI18n.publishBlogWarning
				: shareDialogI18n.updateBlogWarning;
		} else {
			// The PublishModal can only display on pages and blogs, so
			// contentType === 'page'
			return isUnpublishedDraft
				? shareDialogI18n.publishPageWarning
				: shareDialogI18n.updatePageWarning;
		}
	})();

	return (
		<InfoBox
			text={formatMessage(shareDialogInfoTextI18n)}
			beforeLabel="information"
			BeforeIcon={EditorPanelIcon}
		/>
	);
};
